/**
 * Shared Discount Calculation Logic
 * 
 * This module provides a single source of truth for discount calculations
 * used by both the client (cart store) and server (worker).
 */

import { roundCurrency } from './pricing';

/**
 * Options for discount calculation
 */
export interface DiscountCalculationOptions {
    fixedAmount?: number;    // Fixed £ discount applied first
    percentage?: number;     // Percentage discount applied on remainder
}

/**
 * Result of discount calculation with breakdown
 */
export interface DiscountCalculationResult {
    totalDiscount: number;      // Total discount amount
    fixedDiscount: number;      // Portion from fixed amount
    percentageDiscount: number; // Portion from percentage
    finalTotal: number;         // Subtotal minus total discount
}

/**
 * Calculate the discount amount based on subtotal, fixed amount, and/or percentage.
 * Fixed amount is applied first, then percentage on the remainder.
 * This is the canonical implementation used by both client and server.
 * 
 * @param subtotal - The order subtotal before discount
 * @param options - Either a number (percentage for backward compatibility) or DiscountCalculationOptions
 * @returns The calculated discount amount, rounded to 2 decimal places
 */
export function calculateDiscountAmount(
    subtotal: number, 
    options: number | DiscountCalculationOptions
): number {
    const result = calculateDiscountBreakdown(subtotal, options);
    return result.totalDiscount;
}

/**
 * Calculate the discount with full breakdown (fixed + percentage portions).
 * Fixed amount is applied first, then percentage on the remainder.
 * 
 * @param subtotal - The order subtotal before discount
 * @param options - Either a number (percentage for backward compatibility) or DiscountCalculationOptions
 * @returns Full breakdown of discount calculation
 */
export function calculateDiscountBreakdown(
    subtotal: number,
    options: number | DiscountCalculationOptions
): DiscountCalculationResult {
    // Default result for invalid inputs
    const emptyResult: DiscountCalculationResult = {
        totalDiscount: 0,
        fixedDiscount: 0,
        percentageDiscount: 0,
        finalTotal: roundCurrency(Math.max(0, subtotal))
    };

    // Validate subtotal
    if (!Number.isFinite(subtotal) || subtotal <= 0) {
        return emptyResult;
    }

    // Normalize options - support backward compatibility with just percentage number
    const opts: DiscountCalculationOptions = typeof options === 'number' 
        ? { percentage: options } 
        : options;

    const fixedAmount = opts.fixedAmount;
    const percentage = opts.percentage;

    let remainingSubtotal = subtotal;
    let fixedDiscount = 0;
    let percentageDiscount = 0;

    // Step 1: Apply fixed amount first
    if (fixedAmount !== undefined && Number.isFinite(fixedAmount) && fixedAmount > 0) {
        fixedDiscount = roundCurrency(Math.min(fixedAmount, remainingSubtotal));
        remainingSubtotal = roundCurrency(remainingSubtotal - fixedDiscount);
    }

    // Step 2: Apply percentage on remainder
    if (percentage !== undefined && Number.isFinite(percentage) && percentage > 0 && remainingSubtotal > 0) {
        const effectivePercentage = Math.min(percentage, 100);
        percentageDiscount = roundCurrency(remainingSubtotal * (effectivePercentage / 100));
        remainingSubtotal = roundCurrency(remainingSubtotal - percentageDiscount);
    }

    const totalDiscount = roundCurrency(fixedDiscount + percentageDiscount);
    const finalTotal = roundCurrency(Math.max(0, subtotal - totalDiscount));

    return {
        totalDiscount,
        fixedDiscount,
        percentageDiscount,
        finalTotal
    };
}

/**
 * Format discount breakdown for display in emails, order views, etc.
 * Shows "£X off + Y% off" format when both are present, or just one if only one is set.
 * 
 * @param fixedAmount - The fixed £ discount amount (optional)
 * @param percentage - The percentage discount (optional)
 * @returns Human-readable discount description, or empty string if no discount
 */
export function formatDiscountBreakdown(
    fixedAmount?: number | null,
    percentage?: number | null
): string {
    const parts: string[] = [];
    
    if (fixedAmount !== undefined && fixedAmount !== null && fixedAmount > 0) {
        parts.push(`£${fixedAmount.toFixed(2)} off`);
    }
    
    if (percentage !== undefined && percentage !== null && percentage > 0) {
        parts.push(`${percentage}% off`);
    }
    
    return parts.join(' + ');
}
